<template>
  <div class="knight-treasure-scene">
    <span v-for="shield in equipment.shields" :key="shield.owner" class="dropped-knight-shield" :class="{ 'is-gold': shield.gold }" :style="{ left: shield.x + 'px', top: shield.y + 'px' }" aria-label="落在地上的盾牌"><i></i></span>
    <button ref="chest" type="button" class="treasure-chest" :class="{ 'is-open': opened }" :disabled="active" :aria-label="active ? status : '爭奪皇冠，召集盔甲武士對戰'" @click="summon">
      <span class="prize-crown" aria-hidden="true"><i></i><b></b></span>
      <span v-if="opened" class="chest-reward" aria-hidden="true">✦</span>
    </button>
  </div>
</template>

<script>
import { knights, equipment } from './knightArena'
export default {
  name: 'KnightTreasure',
  data () { return { equipment, active: false, opened: false, status: '點擊皇冠，展開對戰', timers: [], fighters: [] } },
  beforeDestroy () {
    this.timers.forEach(timer => window.clearTimeout(timer))
    this.fighters.forEach(knight => {
      if (!knights.has(knight)) return
      knight.$el.style.removeProperty('--combat-tempo')
      knight.fighting = knight.victorious = knight.arenaLocked = false
      knight.sneaking = false
      knight.opponent = knight.position = null
    })
  },
  methods: {
    later (callback, delay) { this.timers.push(window.setTimeout(callback, delay)) },
    summon () {
      if (this.active) return
      const fighters = [...knights].filter(knight => knight.$el.getClientRects().length)
      if (fighters.length < 2 || fighters.some(knight => knight.fighting || knight.forming || knight.dragging || knight.arenaLocked)) {
        this.status = '請等武士準備好，再點擊皇冠'
        return
      }
      this.fighters = fighters
      this.active = true
      this.opened = false
      this.status = '皇冠發出光芒，武士集結！'
      fighters.forEach((knight, index) => {
        window.clearTimeout(knight.assemblyTimer)
        window.clearTimeout(knight.victoryTimer)
        knight.arenaLocked = true
        knight.victorious = knight.forming = false
        knight.assembled = true
        knight.awakenClicks = 3
        // Freeze the current screen position before moving toward the arena.
        const rect = knight.$el.getBoundingClientRect()
        knight.position = { x: rect.left, y: rect.top }
      })
      // Fighters independently pursue nearby rivals or flank an occupied opponent.
      const exchange = () => {
        if (!this.active || fighters.some(knight => !knights.has(knight))) return
        const locations = new Map(fighters.map(knight => [knight, knight.$el.getBoundingClientRect()]))
        fighters.forEach(knight => {
          if (knight.fetchingShield || ['dodge', 'poke', 'lance-dive', 'charge', 'thrust', 'shield-retreat', 'shield-throw', 'blade-retreat', 'blade-wave'].includes(knight.skill)) return
          const own = locations.get(knight)
          const rivals = fighters.filter(other => other !== knight)
          const distracted = rivals.filter(other => other.opponent && other.opponent !== knight && other.attackReady)
          const ambush = distracted.length > 0 && Math.random() < .55
          const target = ambush
            ? distracted[Math.floor(Math.random() * distracted.length)]
            : rivals.sort((a, b) => {
              const first = locations.get(a), second = locations.get(b)
              return Math.hypot(first.left - own.left, first.top - own.top) - Math.hypot(second.left - own.left, second.top - own.top)
            })[Math.random() < .75 ? 0 : Math.floor(Math.random() * rivals.length)]
          const other = locations.get(target)
          const retreat = !ambush && knight.attackReady && Math.random() < .25
          const targetFacingLeft = target.opponent && locations.has(target.opponent)
            ? locations.get(target.opponent).left < other.left : own.left < other.left
          const approachSide = ambush ? (targetFacingLeft ? 1 : -1) : (own.left < other.left ? -1 : 1)
          const gap = retreat ? 230 : 140 + Math.random() * 15
          const goalX = other.left + approachSide * gap
          const goalY = other.top + (Math.random() - .5) * (retreat ? 55 : 12)
          const dx = goalX - own.left, dy = goalY - own.top
          const length = Math.max(1, Math.hypot(dx, dy))
          const step = Math.min(1, (300 + Math.random() * 160) / length)
          knight.position = {
            x: Math.max(50, Math.min(window.innerWidth - 158, own.left + dx * step)),
            y: Math.max(140, Math.min(window.innerHeight - 200, own.top + dy * step))
          }
          knight.opponent = target
          knight.sneaking = ambush
          knight.$el.style.setProperty('--combat-tempo', (1.8 + Math.random() * 1.4) + 's')
        })
      }
      this.later(() => {
        this.status = '自由亂戰開始！'
        fighters.forEach((knight, index) => { knight.opponent = fighters[(index + 1) % fighters.length]; knight.fighting = true })
        exchange()
      }, 100)
      let exchangeTime = 1100
      while (exchangeTime < 17000) {
        this.later(exchange, exchangeTime)
        exchangeTime += 850 + Math.random() * 350
      }
      this.later(() => {
        const winner = fighters[Math.floor(Math.random() * fighters.length)]
        fighters.forEach(knight => {
          knight.fighting = false
          knight.sneaking = false
          knight.$el.style.removeProperty('--combat-tempo')
          knight.opponent = null
          if (knight !== winner) knight.sufferDefeat()
        })
        this.later(() => { winner.victorious = true }, 1500)
        this.status = '勝者前往領取皇冠！'
        this.later(() => {
          winner.victorious = false
          const chest = this.$refs.chest.getBoundingClientRect()
          winner.position = { x: chest.right + 12, y: Math.max(30, chest.top - 25) }
        }, 2400)
        this.later(() => {
          this.opened = true
          fighters.forEach(knight => { knight.crowned = knight === winner })
          winner.victorious = true
          this.status = '勝者戴上皇冠，化身黃金盔甲！'
        }, 3900)
        this.later(() => {
          fighters.forEach(knight => { knight.victorious = false; knight.position = null })
        }, 6500)
        this.later(() => {
          fighters.forEach(knight => { knight.arenaLocked = false })
          this.active = false
          this.status = '黃金冠軍誕生 · 點擊皇冠再次挑戰'
        }, 7900)
      }, 18000)
    }
  }
}
</script>

<style lang="scss" scoped>
.knight-treasure-scene { display: none; }
body.classMasterHead .knight-treasure-scene { display: block; }
.dropped-knight-shield {
  position: fixed; width: 46px; height: 27px; z-index: 1062; pointer-events: none;
  border: 3px ridge #929e8c; border-radius: 50%; rotate: -15deg;
  background: radial-gradient(ellipse at 35% 30%, #d4d9ca, #73806e 50%, #3b4a3e);
  box-shadow: 2px 5px 4px #20302355; animation: shieldGroundDrop .3s ease-out;
  i { position: absolute; inset: 4px 11px; border: 2px solid #485641; border-radius: 50%; background: #b0bba4; }
  &.is-gold { border-color: #caa64e; background: linear-gradient(135deg, #fff0b6, #b48a29); }
}
@keyframes shieldGroundDrop { from { transform: translateY(-25px) rotate(25deg); } to { transform: translateY(0) rotate(0); } }
.treasure-chest {
  position: fixed; left: 22px; top: 110px;
  scale: .6; transform-origin: top left;
  width: 100px; height: 80px; z-index: 1061; padding: 0; border: 0;
  background: none; cursor: pointer; filter: drop-shadow(0 5px 4px #26332c66);
  &:focus-visible { outline: 2px solid #e4c66d; outline-offset: 8px; }
  &:disabled { cursor: wait; }
  .prize-crown {
    position: absolute; inset: 8px 0; filter: drop-shadow(0 0 8px #e4ba5060);
    transition: transform .5s ease, filter .5s ease;
    &::before { content: ''; position: absolute; inset: 0; background: linear-gradient(115deg, #9d651b, #ffe9a0 32%, #cb922c 57%, #fff1aa 78%, #b48024); clip-path: polygon(0 0, 26% 38%, 50% 0, 74% 38%, 100% 0, 88% 100%, 12% 100%); }
    &::after { content: ''; position: absolute; bottom: 0; left: 10px; right: 10px; height: 14px; border: 2px solid #a97722; border-radius: 5px; background: linear-gradient(#ffe99b, #c5912e); }
    i { position: absolute; left: 42px; top: 28px; width: 16px; height: 19px; border: 2px solid #fff0a6; border-radius: 40%; background: radial-gradient(circle at 30% 25%, #ffc8a8, #bd3342 50%, #702532); box-shadow: -25px 7px 0 -4px #339a9d, 25px 7px 0 -4px #339a9d; }
  }
  &:hover:not(:disabled) .prize-crown { transform: translateY(-4px); filter: drop-shadow(0 0 12px #ffe29a); }
  &.is-open .prize-crown { transform: translateY(-8px) scale(1.1); filter: drop-shadow(0 0 16px #ffe29a); }

}
.chest-reward { position: absolute; left: 20px; top: -20px; z-index: 4; font-size: 45px; color: #ffe496; text-shadow: 0 0 15px #efc761; animation: rewardRise 1s ease-out both;
  i { position: absolute; top: 35px; font-size: 23px; font-style: normal; color: #e9c156; text-shadow: 1px 2px #99732e; }
  i:nth-child(1) { left: -10px; } i:nth-child(2) { left: 22px; top: 10px; } i:nth-child(3) { left: 45px; }
}
.treasure-status { position: fixed; top: 290px; left: max(10px, calc((100vw - 1140px) / 4 - 90px)); max-width: 210px; padding: 6px 10px; z-index: 1061; border-radius: 12px; background: #293e37ed; color: #f2e6bd; text-align: center; font-size: 13px; pointer-events: none; }
.treasure-arena { position: fixed; left: 50%; top: 50%; width: 560px; height: 360px; translate: -50% -50%; z-index: 1055; pointer-events: none; border: 2px solid #c3a96790; border-radius: 50%; background: radial-gradient(ellipse, #2e4c4190, #6a806a40 55%, transparent 75%); box-shadow: 0 0 30px #d9c68640;
  span { position: absolute; top: -30px; left: 50%; translate: -50% 0; color: #f3e4b3; background: #293e37; border: 1px solid #c3a967; border-radius: 20px; padding: 5px 20px; }
}
@keyframes rewardRise { from { opacity: 0; transform: translateY(20px) scale(.5); } to { opacity: 1; transform: translateY(0) scale(1); } }
@media (prefers-reduced-motion: reduce) { .chest-reward { animation: none; } .treasure-chest .prize-crown { transition: none; } }
@media (max-width: 1799px), (max-height: 799px) { body.classMasterHead .knight-treasure-scene { display: none; } }
</style>
